import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Assignment,
  DeleteOutline,
  DragIndicator,
  ShoppingCart,
  Warehouse,
} from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSidebarState } from '@/__core/hooks/useSidebarState';
import { AddButtonAutocomplete } from '@/__core/hocs';
import { calculateTableWidth } from '@/utils/tableWidth';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

import { ItemUomAddModal } from '@/modules/inventory/items/components/modals';
import { createPurchaseOrdersForSuppliers } from '../redux/PurchaseOrderCRUD';

import { RequestMaterialItemOption, RequestMaterialLine, RequestMaterialUomOption } from '../types';
import RequestMaterialItemCreateModal from './RequestMaterialItemCreateModal';
import CreatePurchaseOrderModal from './CreatePurchaseOrderModal';
import CreateWarehouseExportModal from './CreateWarehouseExportModal';
import AssignMaterialToOrderModal from './AssignMaterialToOrderModal';

const PAPER_HORIZONTAL_PADDING = 0;

interface RequestMaterialItemsTableProps {
  lines: RequestMaterialLine[];
  itemOptions: RequestMaterialItemOption[];
  uomOptions: RequestMaterialUomOption[];
  companyId: string;
  warehouseId: string;
  companyName?: string;
  warehouseName?: string;
  requiredBy?: string | null;
  orderId?: string;
  orderNo?: string;
  orderName?: string;
  materialRequestId?: string;
  requestedById?: string;
  onChangeLine: (lineId: number, updates: Partial<RequestMaterialLine>) => void;
  onAddItem: () => void;
  onRemoveLine: (lineId: number) => void;
  onReorderLines: (lines: RequestMaterialLine[]) => void;
  onCreateItem: (item: RequestMaterialItemOption) => void;
  onCreateUom: (payload: { name: string; conversion?: { to_uom_id: string; ratio: number } }) => Promise<RequestMaterialUomOption>;
  canEdit?: boolean;
}

interface SortableRowProps {
  line: RequestMaterialLine;
  lines: RequestMaterialLine[];
  itemOptions: RequestMaterialItemOption[];
  uomOptions: RequestMaterialUomOption[];
  companyId: string;
  warehouseId: string;
  companyName?: string;
  checked?: boolean;
  onToggleChecked?: (lineId: number, checked: boolean) => void;
  canEdit?: boolean;
  onChangeLine: (lineId: number, updates: Partial<RequestMaterialLine>) => void;
  onRemoveLine: (lineId: number) => void;
  onCreateItem: (item: RequestMaterialItemOption) => void;
  onCreateUom: (payload: { name: string; conversion?: { to_uom_id: string; ratio: number } }) => Promise<RequestMaterialUomOption>;
}

function SortableRow({
  line,
  lines,
  itemOptions,
  uomOptions,
  companyId,
  warehouseId,
  companyName,
  checked = false,
  onToggleChecked,
  canEdit = true,
  onChangeLine,
  onRemoveLine,
  onCreateItem,
  onCreateUom,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: line.id, disabled: !canEdit });

  const ItemCreateModal = React.useMemo(
    () => (props: { open: boolean; onClose: () => void; onSave: (data: any) => Promise<any>; loading?: boolean }) => (
      <RequestMaterialItemCreateModal {...props} companyId={companyId} companyName={companyName} />
    ),
    [companyId, companyName],
  );

  const UomCreateModal = React.useMemo(
    () => (props: { open: boolean; onClose: () => void; onSave: (data: any) => Promise<any>; loading?: boolean }) => (
      <ItemUomAddModal {...props} defaultToUomId={line.uom_id} />
    ),
    [line.uom_id],
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const displayIndex = lines.findIndex((candidate) => candidate.id === line.id) + 1;

  const groupedItemOptions = React.useMemo(
    () => [...itemOptions].sort((left, right) => {
      const leftSupplier = left.supplier_name?.trim() || 'Chưa có nhà cung cấp';
      const rightSupplier = right.supplier_name?.trim() || 'Chưa có nhà cung cấp';

      return leftSupplier.localeCompare(rightSupplier, 'vi')
        || (left.code || '').localeCompare(right.code || '', 'vi')
        || (left.name || '').localeCompare(right.name || '', 'vi');
    }),
    [itemOptions],
  );
  const selectedItem = itemOptions.find((item) => item.id === line.item_id) || null;
  const selectedUom = uomOptions.find((uom) => uom.id === line.uom_id) || null;
  const supplierName = selectedItem?.supplier_name?.trim() || 'Chưa có nhà cung cấp';

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover .delete-icon': {
          opacity: 1,
        },
      }}
    >
      <TableCell style={{ width: '60px', minWidth: '60px', maxWidth: '60px', paddingLeft: '8px', position: 'relative' }}>
        <IconButton
          size='small'
          {...attributes}
          {...listeners}
          sx={{
            position: 'absolute',
            left: 0,
            top: '5px',
            zIndex: 1,
            cursor: 'grab',
            p: 0,
            minWidth: 'auto',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <DragIndicator fontSize='small' sx={{ color: '#9ca3af' }} />
        </IconButton>
        <Box sx={{ alignItems: 'center' }}>
          <Typography
            sx={{
              marginRight: 'auto',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: '#6b7280',
              textAlign: 'center',
            }}
          >
            {displayIndex}
          </Typography>
        </Box>
      </TableCell>
      <TableCell style={{ width: '52px', minWidth: '52px', maxWidth: '52px', textAlign: 'center' }}>
        <Checkbox
          size='small'
          checked={checked}
          onChange={(event) => onToggleChecked?.(line.id, event.target.checked)}
          sx={{ p: 0.5 }}
        />
      </TableCell>
      <TableCell style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }}>
        <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
          {selectedItem?.code || line.item_code || '—'}
        </Typography>
      </TableCell>
      <TableCell style={{ width: '260px', minWidth: '260px', maxWidth: '260px' }}>
        <AddButtonAutocomplete
            options={groupedItemOptions}
            value={selectedItem}
            getOptionLabel={(option) => {
              if (!option) {
                return '';
                }

              return option.name || '';
            }}
            renderOption={(props, option) => (
              <Box
                component='li'
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                  py: 0.75,
                }}
              >
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', minWidth: 0 }} noWrap>
                  {option.name}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', flexShrink: 0, ml: 'auto', textAlign: 'right', pl: 2 }}>
                  {option?.supplier_name?.trim() || 'Chưa có nhà cung cấp'}
                </Typography>
              </Box>
            )}
            onChange={(_event, newValue) => {
              if (!canEdit) {
                return;
              }
              const nextItem = newValue as RequestMaterialItemOption | null;
              onChangeLine(line.id, {
                item_id: nextItem?.id || '',
                item_code: nextItem?.code || '',
                item_name: nextItem?.name || '',
                uom_id: nextItem?.unit_id || line.uom_id || '',
                uom_name: nextItem?.unit_name || line.uom_name || '',
              });
            }}
            CustomDialog={ItemCreateModal}
            onAddNewCustom={async (createdItem: RequestMaterialItemOption) => {
              if (!canEdit) {
                return createdItem;
              }
              onChangeLine(line.id, {
                item_id: createdItem.id,
                item_code: createdItem.code,
                item_name: createdItem.name,
                uom_id: createdItem.unit_id || '',
                uom_name: createdItem.unit_name || '',
              });
              onCreateItem(createdItem);
              return createdItem;
            }}
            placeholder='Chọn hoặc tạo vật tư'
            noOptionsText='Không tìm thấy vật tư'
            disabled={!canEdit}
            canAddNew={canEdit}
            fullWidth
            sx={{
              '& .MuiInputBase-root': {
                height: '30px',
                minHeight: '30px',
                maxHeight: '30px',
                backgroundColor: 'transparent',
                overflow: 'visible',
                border: 'none !important',
                borderBottom: '1px solid #d1d5db',
                borderRadius: 0,
                color: '#374151 !important',
                '&:hover': {
                  borderBottom: '1px solid #9ca3af',
                },
                '&.Mui-focused': {
                  borderBottom: '1px solid #3b82f6 !important',
                  border: 'none !important',
                },
              },
              '& .MuiInputBase-input': {
                color: '#374151 !important',
                fontSize: '0.875rem',
                padding: '6px 0',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
              '& .MuiAutocomplete-clearIndicator': {
                display: 'none',
              },
              '& .MuiAutocomplete-popupIndicator': {
                display: 'none',
              },
            }}
          />
      </TableCell>
      <TableCell style={{ width: '190px', minWidth: '190px', maxWidth: '190px' }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#4b5563' }} noWrap title={supplierName}>
          {supplierName}
        </Typography>
      </TableCell>
      <TableCell style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
        <TextField
          size='small'
          type='number'
          value={line.requested_qty || ''}
          onChange={(event) => {
            if (!canEdit) {
              return;
            }
            const raw = parseFloat(event.target.value);
            onChangeLine(line.id, { requested_qty: isNaN(raw) ? 0 : Math.round(raw * 100) / 100 });
          }}
          inputProps={{ min: 0, step: 0.01 }}
          disabled={!canEdit}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              height: '30px',
              minHeight: '30px',
              maxHeight: '30px',
              backgroundColor: 'transparent',
              overflow: 'visible',
              border: 'none !important',
              borderBottom: '1px solid #d1d5db',
              borderRadius: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
            '& .MuiInputBase-input': {
              padding: '6px 0',
            },
          }}
        />
      </TableCell>
      <TableCell style={{ width: '170px', minWidth: '170px', maxWidth: '170px' }}>
        <AddButtonAutocomplete
              options={uomOptions}
              value={selectedUom}
              onChange={(_event, newValue) => {
                if (!canEdit) {
                  return;
                }
                const nextUom = newValue as RequestMaterialUomOption | null;
                onChangeLine(line.id, {
                  uom_id: nextUom?.id || '',
                  uom_name: nextUom?.name || '',
                });
              }}
              CustomDialog={UomCreateModal}
              onAddNewCustom={async (payload) => {
                if (!canEdit) {
                  return selectedUom || { id: '', name: '' };
                }
                const created = await onCreateUom(payload);
                onChangeLine(line.id, { uom_id: created.id, uom_name: created.name });
                return created;
              }}
              placeholder='Chọn đơn vị tính'
              noOptionsText='Không tìm thấy đơn vị tính'
              disabled={!canEdit}
              canAddNew={canEdit}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  height: '30px',
                  minHeight: '30px',
                  maxHeight: '30px',
                  backgroundColor: 'transparent',
                  overflow: 'visible',
                  border: 'none !important',
                  borderBottom: '1px solid #d1d5db',
                  borderRadius: 0,
                  color: '#374151 !important',
                  '&:hover': {
                    borderBottom: '1px solid #9ca3af',
                  },
                  '&.Mui-focused': {
                    borderBottom: '1px solid #3b82f6 !important',
                    border: 'none !important',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#374151 !important',
                  fontSize: '0.875rem',
                  padding: '6px 0',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
                '& .MuiAutocomplete-clearIndicator': {
                  display: 'none',
                },
                '& .MuiAutocomplete-popupIndicator': {
                  display: 'none',
                },
              }}
            />
      </TableCell>
      <TableCell style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}>
        <IconButton
          className='delete-icon'
          size='small'
          onClick={() => onRemoveLine(line.id)}
          disabled={!canEdit}
          sx={{
            color: '#ef4444',
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          <DeleteOutline fontSize='small' />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

const tableStyles = sharedFormStyles.orderItemsTableStyles;

export default function RequestMaterialItemsTable({
  lines,
  itemOptions,
  uomOptions,
  companyId,
  warehouseId,
  companyName,
  warehouseName,
  requiredBy,
  orderId,
  orderNo,
  orderName,
  materialRequestId,
  requestedById,
  onChangeLine,
  onAddItem,
  onRemoveLine,
  onReorderLines,
  onCreateItem,
  onCreateUom,
  canEdit = true,
}: RequestMaterialItemsTableProps) {
  const [tableWidth, setTableWidth] = React.useState<number>(0);
  const [checkedByLineId, setCheckedByLineId] = React.useState<Record<number, boolean>>({});
  const [purchaseOrderModalOpen, setPurchaseOrderModalOpen] = React.useState(false);
  const [purchaseOrderCreating, setPurchaseOrderCreating] = React.useState(false);
  const [warehouseExportModalOpen, setWarehouseExportModalOpen] = React.useState(false);
  const [assignOrderModalOpen, setAssignOrderModalOpen] = React.useState(false);
  const sidebarOpen = useSidebarState();

  const displayLines = React.useMemo(
    () => lines.filter((line) => line.line_type === 'item'),
    [lines],
  );

  const itemLineIds = React.useMemo(
    () => displayLines.map((line) => line.id),
    [displayLines],
  );

  const checkedItemCount = React.useMemo(
    () => itemLineIds.filter((lineId) => Boolean(checkedByLineId[lineId])).length,
    [checkedByLineId, itemLineIds],
  );

  const areAllItemsChecked = itemLineIds.length > 0 && checkedItemCount === itemLineIds.length;
  const areSomeItemsChecked = checkedItemCount > 0 && checkedItemCount < itemLineIds.length;

  const handleToggleAllItems = (checked: boolean) => {
    if (!canEdit) {
      return;
    }
    setCheckedByLineId(
      itemLineIds.reduce<Record<number, boolean>>((accumulator, lineId) => {
        accumulator[lineId] = checked;
        return accumulator;
      }, {}),
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  React.useEffect(() => {
    setCheckedByLineId((current) => {
      const validIds = new Set(lines.filter((line) => line.line_type === 'item').map((line) => line.id));
      return Object.fromEntries(Object.entries(current).filter(([key]) => validIds.has(Number(key))));
    });
  }, [lines]);

  React.useEffect(() => {
    const handleResize = () => {
      const finalWidth = calculateTableWidth({
        windowWidth: window.innerWidth - 34,
        sidebarOpen,
      });
      setTableWidth(finalWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const handleCreatePurchaseOrders = async (orders: Array<{ supplierId: string; supplierName: string; requiredBy: string | null; items: Array<{ lineId: number; itemId: string; itemCode: string; itemName: string; requestedQty: number; purchaseQty: number; uomId: string; uomName: string }> }>) => {
    if (!companyId || !warehouseId) {
      throw new Error('Công ty và kho là bắt buộc');
    }

    setPurchaseOrderCreating(true);
    try {
      await createPurchaseOrdersForSuppliers(
        orders.map((order) => ({
          supplierName: order.supplierName,
          supplierId: order.supplierId,
          items: order.items.map((item) => ({
            itemId: item.itemId,
            uomId: item.uomId,
            requestedQty: item.requestedQty,
            orderedQty: item.purchaseQty,
            unitPrice: 0, // Default unit price, can be updated later
            sourceRequestLineId: String(item.lineId),
          })),
          companyId,
          warehouseId,
          materialRequestId,
          requestedById,
        })),
      );
    } finally {
      setPurchaseOrderCreating(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canEdit) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    if (Number(active.id) < 0 || Number(over.id) < 0) {
      return;
    }

    const oldIndex = displayLines.findIndex((line) => line.id === active.id);
    const newIndex = displayLines.findIndex((line) => line.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const nextLines = arrayMove(displayLines, oldIndex, newIndex).map((line, index) => ({
      ...line,
      sort_order: index + 1,
    }));
    onReorderLines(nextLines);
  };

  return (
    <Paper
      sx={{
        padding: '0px',
        borderRadius: 1,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
        marginBottom: '24px',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <TableContainer
        sx={tableStyles.tableContainer(tableWidth, PAPER_HORIZONTAL_PADDING)}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={displayLines.map((line) => line.id)} strategy={verticalListSortingStrategy}>
            <Table size='small' sx={tableStyles.table}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell style={{ width: '30px', minWidth: '30px', maxWidth: '40px' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    <Box sx={{ textAlign: 'center' }}>#</Box>
                  </TableCell>
                  <TableCell style={{ width: '32px', minWidth: '32px', maxWidth: '32px' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                      <Checkbox
                        size='small'
                        checked={areAllItemsChecked}
                        indeterminate={areSomeItemsChecked}
                        disabled={itemLineIds.length === 0 || !canEdit}
                        onChange={(event) => handleToggleAllItems(event.target.checked)}
                        sx={{ p: 0.25 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    Mã vật tư
                  </TableCell>
                  <TableCell style={{ width: '260px', minWidth: '260px', maxWidth: '100%' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    Tên vật tư
                  </TableCell>
                  <TableCell style={{ width: '190px', minWidth: '190px', maxWidth: '190px' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    Nhà cung cấp
                  </TableCell>
                  <TableCell style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    SL
                  </TableCell>
                  <TableCell style={{ width: '170px', minWidth: '70px', maxWidth: '170px' }} sx={{ fontWeight: 600, color: '#374151' }}>
                    ĐVT
                  </TableCell>
                  <TableCell style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayLines.map((line) => (
                  <SortableRow
                    key={line.id}
                    line={line}
                    lines={displayLines}
                    itemOptions={itemOptions}
                    uomOptions={uomOptions}
                    companyId={companyId}
                    warehouseId={warehouseId}
                    companyName={companyName}
                    checked={Boolean(checkedByLineId[line.id])}
                    onToggleChecked={(lineId, nextChecked) => {
                      if (!canEdit) {
                        return;
                      }
                      setCheckedByLineId((current) => ({ ...current, [lineId]: nextChecked }));
                    }}
                    canEdit={canEdit}
                    onChangeLine={onChangeLine}
                    onRemoveLine={onRemoveLine}
                    onCreateItem={onCreateItem}
                    onCreateUom={onCreateUom}
                  />
                ))}
              </TableBody>
            </Table>
          </SortableContext>
        </DndContext>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='outlined'
            startIcon={<Add />}
            onClick={onAddItem}
            disabled={!canEdit}
            size='small'
            sx={{
              borderColor: '#d1d5db',
              color: '#6b7280',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb',
              },
            }}
          >
            Thêm item
          </Button>
        </Box>

        {checkedItemCount > 0 && canEdit && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant='contained'
              startIcon={<ShoppingCart />}
              size='small'
              onClick={() => setPurchaseOrderModalOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              Tạo phiếu mua hàng ({checkedItemCount})
            </Button>
            <Button
              variant='contained'
              startIcon={<Warehouse />}
              size='small'
              onClick={() => setWarehouseExportModalOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#059669',
                '&:hover': { backgroundColor: '#047857' },
              }}
            >
              Tạo phiếu xuất kho ({checkedItemCount})
            </Button>
            <Button
              variant='contained'
              startIcon={<Assignment />}
              size='small'
              onClick={() => setAssignOrderModalOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#7c3aed',
                '&:hover': { backgroundColor: '#6d28d9' },
              }}
            >
              Gán vật liệu cho ĐH ({checkedItemCount})
            </Button>
          </Box>
        )}
      </Box>

      {(() => {
        const selectedLines = lines.filter((line) => line.line_type === 'item' && Boolean(checkedByLineId[line.id]));
        const availableQtyByLineId: Record<number, number> = {};

        return (
          <>
            <CreatePurchaseOrderModal
              open={purchaseOrderModalOpen}
              onClose={() => setPurchaseOrderModalOpen(false)}
              selectedLines={selectedLines}
              itemOptions={itemOptions}
              uomOptions={uomOptions}
              defaultRequiredBy={requiredBy}
              onConfirm={handleCreatePurchaseOrders}
              loading={purchaseOrderCreating}
            />
            <CreateWarehouseExportModal
              open={warehouseExportModalOpen}
              onClose={() => setWarehouseExportModalOpen(false)}
              selectedLines={selectedLines}
              itemOptions={itemOptions}
              uomOptions={uomOptions}
              warehouseId={warehouseId}
              warehouseName={warehouseName}
              companyName={companyName}
              defaultExportDate={requiredBy}
              availableQtyByLineId={availableQtyByLineId}
              onConfirm={() => setWarehouseExportModalOpen(false)}
            />
            <AssignMaterialToOrderModal
              open={assignOrderModalOpen}
              onClose={() => setAssignOrderModalOpen(false)}
              selectedLines={selectedLines}
              itemOptions={itemOptions}
              uomOptions={uomOptions}
              orderId={orderId}
              orderNo={orderNo}
              orderName={orderName}
              availableQtyByLineId={availableQtyByLineId}
              onConfirm={() => setAssignOrderModalOpen(false)}
            />
          </>
        );
      })()}
    </Paper>
  );
}
